import React, { useCallback, useEffect, useState } from 'react'
import api from '../../../../../services/api'
import Form, {
  Input,
  Select,
  SelectSearch,
  Textarea
} from '../../../../../components/Form'
import { useHistory } from 'react-router-dom'
import { useToast } from '../../../../../hooks/toast'
import { useLoading } from '../../../../../hooks/loading'
import { useUpdateDataTable } from '../../../../../hooks/dataTable'
import { apiCreate, apiUpdate } from '../../domain/api'
import {
  nameActions,
  ROUTE_AGENCY_PRIORITY_ENUM,
  ROUTE_AGENCY_PRIORITY_MAP
} from '../../domain/info'
import { FormContainer } from './styles'
import { listAgenciesByPromoterOptions } from '../../../Agencies/domain/api'

type IsOpenInModalProps = {
  handleOnClose: () => void
  route_id: number
}

export type RouteAgencyData = {
  id?: string
  route_id: number
  agency_id: number
  priority: ROUTE_AGENCY_PRIORITY_ENUM
  observation?: string | null
  agency?: {
    name: string
  }
}

type RouteAgencyResponseData = {
  route_id: number
  agency_id: number
  priority: ROUTE_AGENCY_PRIORITY_ENUM
  observation?: string | null
}

type TypesFormProps = {
  isOpenInModal?: false | IsOpenInModalProps
  initialValues?: RouteAgencyData & {
    idUpdate: number
  }
  typeForm: 'create' | 'update'
  promoter_id: number // to search agencies options
}

export const FormRouteAgency = ({
  isOpenInModal,
  initialValues,
  typeForm,
  promoter_id
}: TypesFormProps): JSX.Element => {
  const { addToast } = useToast()
  const history = useHistory()
  const { updateDataTable } = useUpdateDataTable()

  const [agenciesOptions, setAgenciesOptions] = useState([])

  const loadAgencies = useCallback(async (promoter_id: number) => {
    if (!promoter_id) return
    try {
      const responseAgencies = await api.get(
        listAgenciesByPromoterOptions(String(promoter_id))
      )
      setAgenciesOptions(
        responseAgencies.data?.map((item: { name: string; id: number }) => ({
          name: `${item.name}`,
          value: item.id
        }))
      )
    } catch (error) {}
  }, [])

  const [defaultValues, setDefaultValues] = useState<RouteAgencyResponseData>()

  useEffect(() => {
    if (initialValues) {
      setDefaultValues({
        agency_id: initialValues.agency_id,
        priority: initialValues.priority,
        route_id: initialValues.route_id,
        observation: initialValues.observation
      })
    }
  }, [initialValues, typeForm])

  useEffect(() => {
    if (promoter_id) {
      loadAgencies(promoter_id)
    }
  }, [promoter_id, loadAgencies])

  const { activeLoading, disableLoading } = useLoading()

  const onSubmitForm = async (data: RouteAgencyResponseData) => {
    const id = initialValues?.idUpdate

    try {
      if (typeForm === 'create') {
        if (isOpenInModal) {
          try {
            const dataCreate = {
              ...data,
              route_id: isOpenInModal.route_id
            }
            activeLoading()
            await api.post(apiCreate(), dataCreate)
            disableLoading()
            updateDataTable()
            addToast({
              type: 'success',
              title: 'Registro criado',
              description: 'Registro criado com sucesso'
            })
            isOpenInModal.handleOnClose()
          } catch (error) {
            addToast({
              type: 'error',
              title: 'Erro ao adicionar o registro',
              description:
                'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
            })
            disableLoading()
            updateDataTable()
          }
        } else {
          try {
            const dataCreate = {
              ...data
            }
            activeLoading()
            await api.post(apiCreate(), dataCreate)
            disableLoading()
            updateDataTable()
            addToast({
              type: 'success',
              title: 'Registro criado',
              description: 'Registro criado com sucesso'
            })
            history.push(nameActions.read.to)
          } catch (error) {
            addToast({
              type: 'error',
              title: 'Erro ao adicionar o registro',
              description:
                'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
            })
            disableLoading()
            updateDataTable()
          }
        }
      } else {
        if (isOpenInModal) {
          const dataUpdate = {
            ...data,
            route_id: isOpenInModal.route_id
          }

          try {
            activeLoading()
            await api.put(apiUpdate(String(id)), dataUpdate)
            updateDataTable()
            disableLoading()
            addToast({
              type: 'success',
              title: 'Registro atualizado',
              description: 'Registro alterado com sucesso'
            })
            isOpenInModal.handleOnClose()
          } catch (error) {
            history.push(nameActions.read.to)
            addToast({
              type: 'error',
              title: 'Erro ao atualizar o registro',
              description:
                'Ocorreu um erro ao fazer a atualização, por favor, tente novamente.'
            })
          }
        } else {
          const dataUpdate = {
            ...data
          }

          try {
            activeLoading()
            await api.put(apiUpdate(String(id)), dataUpdate)
            updateDataTable()
            disableLoading()
            history.push(nameActions.read.to)
            addToast({
              type: 'success',
              title: 'Registro atualizado',
              description: 'Registro alterado com sucesso'
            })
          } catch (error) {
            history.push(nameActions.read.to)
            addToast({
              type: 'error',
              title: 'Erro ao atualizar o registro',
              description:
                'Ocorreu um erro ao fazer a atualização, por favor, tente novamente.'
            })
          }
        }
      }
      disableLoading()
    } catch (err) {
      if (typeForm === 'create') {
        addToast({
          type: 'error',
          title: 'Erro no cadastro',
          description:
            'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
        })
        if (isOpenInModal) isOpenInModal.handleOnClose()
      }
    }
  }

  return (
    <Form onSubmit={onSubmitForm} defaultValues={defaultValues}>
      <>
        <div className="card mb-5 mb-xl-10">
          <FormContainer className="form">
            <div className="row mb-5">
              {typeForm === 'create' ? (
                <SelectSearch
                  name="agency_id"
                  label="Agência"
                  className="col-md-4"
                  options={agenciesOptions}
                  blank
                  defaultValue=""
                  placeholder="Digite para buscar agência..."
                  noOptionsMessage="Nenhuma agência encontrada"
                  rules={{ required: true }}
                />
              ) : (
                <Input
                  label="Agência"
                  name="agency_name"
                  value={initialValues.agency.name}
                  className="col-md-4"
                  disabled
                ></Input>
              )}
              <Select
                name="priority"
                label="Prioridade"
                rules={{ required: true }}
                className="col-md-4"
                options={Object.entries(ROUTE_AGENCY_PRIORITY_MAP).map(
                  ([key, label]) => ({
                    name: label,
                    value: key
                  })
                )}
                controlled
                blank
              />

              <Textarea
                name="observation"
                label="Observações"
                className="col-md-12"
              />
            </div>
          </FormContainer>
          <div className="card-footer d-flex justify-content-end py-6 px-9">
            <button type="submit" className="btn btn-primary">
              Salvar
            </button>
          </div>
        </div>
      </>
    </Form>
  )
}
